import { GiftCardSettings, defaultGiftCardSettings } from './giftCardSettingsService';
import { GiftCardEmailSettings, defaultGiftCardEmailSettings } from './giftCardEmailSettingsService';

interface GiftCardEmailData {
  giftCardCode: string;
  amount: number;
  design: GiftCardSettings;
  businessName: string;
  businessLogoUrl?: string | null;
  recipientName?: string;
  customMessage?: string;
  isForRecipient: boolean; // true if recipient exists, false if only purchaser
  giftCardImageDataUrl?: string; // Base64 image of the gift card
  emailSettings?: GiftCardEmailSettings; // Customizable email text settings
}

/**
 * Generate HTML email template for gift card delivery
 */
export function generateGiftCardEmailHTML(data: GiftCardEmailData): string {
  const settings = {
    ...defaultGiftCardSettings,
    ...data.design
  } as GiftCardSettings;

  // Get email settings or use defaults
  const emailSettings = data.emailSettings || defaultGiftCardEmailSettings;

  // Helper function to replace placeholders in text
  const replacePlaceholders = (text: string): string => {
    return text
      .replace(/{business_name}/g, data.businessName)
      .replace(/{recipient_name}/g, data.recipientName || '')
      .replace(/{gift_card_code}/g, data.giftCardCode)
      .replace(/{amount}/g, `$${data.amount.toFixed(2)}`);
  };

  // Get font family for email
  const getEmailFontFamily = () => {
    switch (emailSettings.font_family) {
      case 'serif':
        return 'Georgia, "Times New Roman", serif';
      case 'monospace':
        return '"Courier New", Courier, monospace';
      case 'cursive':
        return '"Comic Sans MS", "Brush Script MT", cursive';
      case 'sans-serif':
      default:
        return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    }
  };

  // Determine text color based on background brightness
  const isLightBackground = (color: string) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  };

  const textColor = isLightBackground(settings.background_color) ? '#1F2937' : '#FFFFFF';
  
  // Get border radius style
  const getBorderRadius = () => {
    switch (settings.border_radius) {
      case 'sharp': return '0px';
      case 'rounded': return '16px';
      case 'pill': return '48px';
      default: return '16px';
    }
  };

  // Get font family
  const getFontFamily = () => {
    switch (settings.font_family) {
      case 'inter': return 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      case 'roboto': return 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      case 'playfair': return '"Playfair Display", Georgia, serif';
      case 'poppins': return 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      case 'montserrat': return 'Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      default: return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    }
  };

  // Logo positioning
  const getLogoStyle = () => {
    const position = settings.logo_position;
    let top = 'auto';
    let bottom = 'auto';
    let left = 'auto';
    let right = 'auto';
    let transform = 'none';

    if (position.startsWith('top-')) {
      top = '32px';
    } else if (position.startsWith('center-')) {
      top = '50%';
      transform = 'translateY(-50%)';
    } else if (position.startsWith('bottom-')) {
      bottom = '32px';
    }

    if (position.endsWith('-left')) {
      left = '32px';
      right = 'auto';
    } else if (position.endsWith('-center') || position === 'center') {
      left = '50%';
      right = 'auto';
      transform = transform === 'translateY(-50%)' ? 'translate(-50%, -50%)' : 'translateX(-50%)';
    } else if (position.endsWith('-right')) {
      right = '32px';
      left = 'auto';
    }

    return { top, bottom, left, right, transform };
  };

  const logoStyle = getLogoStyle();
  const logoSize = settings.logo_size === 'small' ? '32px' : '48px';

  // Build gradient overlay for modern style
  const gradientOverlay = settings.card_style === 'modern' 
    ? `<div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(135deg, ${settings.primary_color} 0%, ${settings.secondary_color} 100%); opacity: 0.1; pointer-events: none;"></div>`
    : '';

  // Decorative elements
  let decorativeElements = '';
  if (settings.show_decorative_elements) {
    if (settings.card_style === 'modern') {
      decorativeElements = `
        <div style="position: absolute; top: 0; right: 0; width: 256px; height: 256px; border-radius: 50%; background: ${settings.primary_color}; opacity: 0.2; filter: blur(60px);"></div>
        <div style="position: absolute; bottom: 0; left: 0; width: 192px; height: 192px; border-radius: 50%; background: ${settings.accent_color}; opacity: 0.2; filter: blur(60px);"></div>
      `;
    } else if (settings.card_style === 'classic') {
      decorativeElements = `<div style="position: absolute; bottom: 0; left: 0; right: 0; height: 8px; background: ${settings.accent_color};"></div>`;
    }
  }

  // Title alignment
  const titleAlign = settings.card_title_position === 'left' ? 'left' : 
                     settings.card_title_position === 'right' ? 'right' : 'center';
  
  // Message alignment
  const messageAlign = settings.greeting_message_position === 'left' ? 'left' : 
                       settings.greeting_message_position === 'right' ? 'right' : 'center';

  // Get greeting text based on recipient/purchaser
  const greetingText = data.isForRecipient
    ? replacePlaceholders(emailSettings.email_greeting_recipient)
    : replacePlaceholders(emailSettings.email_greeting_purchaser);

  // Custom message section
  const customMessageSection = data.customMessage
    ? `
      <div style="margin: 24px 0; padding: 16px; background: #F9FAFB; border-left: 4px solid ${settings.primary_color}; border-radius: 4px;">
        <p style="margin: 0; font-style: italic; color: #4B5563; line-height: 1.6;">
          "${data.customMessage}"
        </p>
      </div>
    `
    : '';

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Your Gift Card from ${data.businessName}</title>
</head>
<body style="margin: 0; padding: 0; font-family: ${getEmailFontFamily()}; background-color: #F3F4F6;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #F3F4F6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width: 600px; background-color: #FFFFFF; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          
          <!-- Header -->
          <tr>
            <td style="padding: 40px 40px 20px; text-align: center; background-color: #FFFFFF;">
              <h1 style="margin: 0; font-size: 28px; font-weight: bold; color: #111827;">
                ${data.isForRecipient 
                  ? replacePlaceholders(emailSettings.email_header_recipient)
                  : replacePlaceholders(emailSettings.email_header_purchaser)}
              </h1>
            </td>
          </tr>

          <!-- Main Content -->
          <tr>
            <td style="padding: 0 40px 40px;">
              <p style="margin: 0 0 24px; font-size: 16px; line-height: 1.6; color: #374151;">
                ${greetingText}
              </p>
              
              <p style="margin: 0 0 32px; font-size: 16px; line-height: 1.6; color: #374151;">
                ${data.isForRecipient
                  ? replacePlaceholders(emailSettings.email_message_recipient)
                  : replacePlaceholders(emailSettings.email_message_purchaser)}
              </p>

              ${customMessageSection}

              <!-- Gift Card Preview -->
              <div style="margin: 32px 0; background-color: #F9FAFB; padding: 24px; border-radius: 8px; text-align: center;">
                ${data.giftCardImageDataUrl && data.giftCardImageDataUrl.length > 100 && data.giftCardImageDataUrl.startsWith('data:image')
                  ? `<img src="${data.giftCardImageDataUrl}" alt="Gift Card" style="max-width: 100%; height: auto; border-radius: ${getBorderRadius()}; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2); display: block; margin: 0 auto;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
                     <div style="display: none; position: relative; width: 100%; max-width: 500px; margin: 0 auto; aspect-ratio: 1.586 / 1; background-color: ${settings.background_color}; border-radius: ${getBorderRadius()}; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);">
                      ${gradientOverlay}
                      ${decorativeElements}
                      ${settings.include_logo && data.businessLogoUrl ? `
                        <div style="position: absolute; ${logoStyle.top !== 'auto' ? `top: ${logoStyle.top};` : ''} ${logoStyle.bottom !== 'auto' ? `bottom: ${logoStyle.bottom};` : ''} ${logoStyle.left !== 'auto' ? `left: ${logoStyle.left};` : ''} ${logoStyle.right !== 'auto' ? `right: ${logoStyle.right};` : ''} transform: ${logoStyle.transform}; z-index: 1;">
                          <img src="${data.businessLogoUrl}" alt="${data.businessName}" style="height: ${logoSize}; max-height: ${settings.logo_max_height}px; width: auto; display: block;" />
                        </div>
                      ` : ''}
                      <div style="position: relative; z-index: 2; height: 100%; display: flex; flex-direction: column; padding: 32px;">
                        <div style="text-align: ${titleAlign}; margin-bottom: 8px;">
                          <h2 style="margin: 0; font-size: 32px; font-weight: bold; color: ${textColor};">
                            ${settings.card_title}
                          </h2>
                        </div>
                        <div style="text-align: ${messageAlign}; margin-bottom: 24px;">
                          <p style="margin: 0; font-size: 14px; color: ${textColor}; opacity: 0.9;">
                            ${greetingText}
                          </p>
                        </div>
                        <div style="flex: 1;"></div>
                        <div style="text-align: center; margin-top: auto; padding-bottom: 16px;">
                          <p style="margin: 0; font-family: 'Courier New', monospace; font-size: 14px; font-weight: 600; color: ${textColor}; letter-spacing: 2px;">
                            ${data.giftCardCode}
                          </p>
                        </div>
                      </div>
                      ${settings.card_style === 'classic' ? `
                        <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 8px; background-color: ${settings.accent_color};"></div>
                      ` : ''}
                    </div>`
                  : `<div style="position: relative; width: 100%; max-width: 500px; margin: 0 auto; aspect-ratio: 1.586 / 1; background-color: ${settings.background_color}; border-radius: ${getBorderRadius()}; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);">
                      ${gradientOverlay}
                      ${decorativeElements}
                      ${settings.include_logo && data.businessLogoUrl ? `
                        <div style="position: absolute; ${logoStyle.top !== 'auto' ? `top: ${logoStyle.top};` : ''} ${logoStyle.bottom !== 'auto' ? `bottom: ${logoStyle.bottom};` : ''} ${logoStyle.left !== 'auto' ? `left: ${logoStyle.left};` : ''} ${logoStyle.right !== 'auto' ? `right: ${logoStyle.right};` : ''} transform: ${logoStyle.transform}; z-index: 1;">
                          <img src="${data.businessLogoUrl}" alt="${data.businessName}" style="height: ${logoSize}; max-height: ${settings.logo_max_height}px; width: auto; display: block;" />
                        </div>
                      ` : ''}
                      <div style="position: relative; z-index: 2; height: 100%; display: flex; flex-direction: column; padding: 32px;">
                        <div style="text-align: ${titleAlign}; margin-bottom: 8px;">
                          <h2 style="margin: 0; font-size: 32px; font-weight: bold; color: ${textColor};">
                            ${settings.card_title}
                          </h2>
                        </div>
                        <div style="text-align: ${messageAlign}; margin-bottom: 24px;">
                          <p style="margin: 0; font-size: 14px; color: ${textColor}; opacity: 0.9;">
                            ${greetingText}
                          </p>
                        </div>
                        <div style="flex: 1;"></div>
                        <div style="text-align: center; margin-top: auto; padding-bottom: 16px;">
                          <p style="margin: 0; font-family: 'Courier New', monospace; font-size: 14px; font-weight: 600; color: ${textColor}; letter-spacing: 2px;">
                            ${data.giftCardCode}
                          </p>
                        </div>
                      </div>
                      ${settings.card_style === 'classic' ? `
                        <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 8px; background-color: ${settings.accent_color};"></div>
                      ` : ''}
                    </div>`
                }
              </div>

              <!-- Gift Card Details -->
              <div style="margin: 32px 0; padding: 24px; background-color: #F9FAFB; border-radius: 8px; border: 2px solid #E5E7EB;">
                <h3 style="margin: 0 0 16px; font-size: 20px; font-weight: 600; color: #111827;">
                  ${replacePlaceholders(emailSettings.email_details_title)}
                </h3>
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                  <tr>
                    <td style="padding: 8px 0; font-size: 16px; color: #6B7280; width: 50%;">
                      ${replacePlaceholders(emailSettings.email_code_label)}
                    </td>
                    <td style="padding: 8px 0; font-size: 16px; font-weight: 600; color: #111827; font-family: 'Courier New', monospace;">
                      ${data.giftCardCode}
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; font-size: 16px; color: #6B7280;">
                      ${replacePlaceholders(emailSettings.email_amount_label)}
                    </td>
                    <td style="padding: 8px 0; font-size: 20px; font-weight: bold; color: #059669;">
                      $${data.amount.toFixed(2)}
                    </td>
                  </tr>
                </table>
              </div>

              <!-- Instructions -->
              <div style="margin: 32px 0; padding: 20px; background-color: #EFF6FF; border-radius: 8px; border-left: 4px solid ${settings.primary_color};">
                <p style="margin: 0 0 12px; font-size: 16px; font-weight: 600; color: #1E40AF;">
                  ${replacePlaceholders(emailSettings.email_instructions_title)}
                </p>
                <ul style="margin: 0; padding-left: 20px; color: #1E3A8A; line-height: 1.8;">
                  <li>${replacePlaceholders(emailSettings.email_instruction_1)}</li>
                  <li>${replacePlaceholders(emailSettings.email_instruction_2)}</li>
                  <li>${replacePlaceholders(emailSettings.email_instruction_3)}</li>
                  <li>${replacePlaceholders(emailSettings.email_instruction_4)}</li>
                </ul>
              </div>

              <!-- Footer -->
              <div style="margin-top: 40px; padding-top: 24px; border-top: 1px solid #E5E7EB; text-align: center;">
                <p style="margin: 0; font-size: 14px; color: #6B7280;">
                  ${replacePlaceholders(emailSettings.email_footer_text)}
                </p>
                <p style="margin: 8px 0 0; font-size: 12px; color: #9CA3AF;">
                  ${replacePlaceholders(emailSettings.email_footer_subtext)}
                </p>
              </div>

            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `;

  return html;
}

